import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import type { HyroxSemanticColors } from 'app/design-tokens/hyrox-semantic-colors';
import type { LeadStatus } from 'app/features/leads/services/gym-finder/types';
import Typography from 'app/ui/components/atoms/typography';

type LeadStatusPillProps = {
  status: LeadStatus;
};

type ToneTokens = {
  background: keyof HyroxSemanticColors;
  border: keyof HyroxSemanticColors;
  text: keyof HyroxSemanticColors;
};

const TONE_TOKENS_BY_STATUS: Record<LeadStatus, ToneTokens> = {
  active: {
    background: 'success_500',
    border: 'success_500',
    text: 'success_600',
  },
  paused: {
    background: 'warnning_500',
    border: 'warnning_600',
    text: 'warnning_600',
  },
  trial: {
    background: 'info_500',
    border: 'info_500',
    text: 'info_600',
  },
};

const PillContainer = styled.span<{ $background: string; $border: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => `${spacing(1)} ${spacing(3)}`};
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  border: 0.0625rem solid ${({ $border }) => $border};
  background: ${({ $background }) => $background};
  white-space: nowrap;
`;

const PillLabel = styled(Typography)`
  font-size: 0.75rem;
  font-weight: ${({ theme: { typography } }) => typography.bold};
  line-height: 1rem;
`;

export const LeadStatusPill = ({ status }: LeadStatusPillProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const toneTokens = TONE_TOKENS_BY_STATUS[status];
  const label = intl.formatMessage({ id: `leads.status.${status}` });

  return (
    <PillContainer
      $background={`${semantic[toneTokens.background]}1f`}
      $border={semantic[toneTokens.border]}
      data-testid={`lead-status-${status}`}
    >
      <PillLabel variant="body3" color={semantic[toneTokens.text]}>
        {label}
      </PillLabel>
    </PillContainer>
  );
};

export default LeadStatusPill;
